import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
    authApi,
    GOOGLE_CLIENT_ID,
    FACEBOOK_APP_ID,
    APPLE_CLIENT_ID,
    APPLE_REDIRECT_URI,
    APPLE_CLIENT,
    isAuthenticated,
    userApi,
    getApiErrorMessage,
} from '../utils/api';
import SimplizeLogo from './SimplizeLogo';
import SimplizeOriginalLogo from './SimplizeOriginalLogo';

const loadScript = (src, id) => {
    return new Promise((resolve, reject) => {
        const existing = document.getElementById(id) || document.querySelector(`script[src="${src}"]`);
        if (existing) {
            if (existing.dataset.loaded === 'true') {
                resolve();
                return;
            }
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', reject);
            return;
        }
        const script = document.createElement('script');
        script.id = id;
        script.src = src;
        script.async = true;
        script.defer = true;
        script.onload = () => { script.dataset.loaded = 'true'; resolve(); };
        script.onerror = reject;
        document.head.appendChild(script);
    });
};

const loadGoogleSDK = () => loadScript('https://accounts.google.com/gsi/client', 'google-gsi');

const loadFacebookSDK = () => {
    return new Promise((resolve) => {
        if (window.FB) {
            resolve();
            return;
        }
        window.fbAsyncInit = () => {
            window.FB.init({
                appId: FACEBOOK_APP_ID,
                cookie: true,
                xfbml: false,
                version: 'v19.0',
            });
            resolve();
        };
        loadScript('https://connect.facebook.net/vi_VN/sdk.js', 'facebook-jssdk').catch(() => resolve());
    });
};

const loadAppleSDK = () => loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js', 'apple-jssdk');

const GoogleIcon = () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
);

const AppleIcon = () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#000">
        <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
);

const PROVIDERS = [
    { id: 'google', label: 'Google', icon: <GoogleIcon /> },
    { id: 'apple', label: 'Apple', icon: <AppleIcon /> },
];

export default function LoginPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const returnUrl = searchParams.get('returnUrl') || '/';
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [step, setStep] = useState('initial');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [sdkReady, setSdkReady] = useState({ google: false, facebook: false, apple: false });

    useEffect(() => {
        if (isAuthenticated()) {
            navigate('/', { replace: true });
            return;
        }

        loadGoogleSDK()
            .then(() => setSdkReady((s) => ({ ...s, google: true })))
            .catch(() => { });

        if (FACEBOOK_APP_ID) {
            loadFacebookSDK()
                .then(() => setSdkReady((s) => ({ ...s, facebook: true })))
                .catch(() => { });
        }

        if (APPLE_CLIENT_ID) {
            loadAppleSDK()
                .then(() => setSdkReady((s) => ({ ...s, apple: true })))
                .catch(() => { });
        }
    }, [navigate]);

    const handleSuccess = useCallback(async (data) => {
        if (data?.accessToken) {
            userApi.clearCached();
            try {
                await authApi.activeFreeTrial();
            } catch (e) {
                console.error('activeFreeTrial failed', e);
            }
            // Full reload so data-loading effects (session list, user profile,
            // websocket auth, …) run against the fresh token instead of the
            // stale pre-login state held by the SPA.
            window.location.replace(returnUrl || '/');
        }
    }, [returnUrl]);

    const handleEmailContinue = (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setError('');
        setStep('password');
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setError('');
        setIsSubmitting(true);
        try {
            const data = await authApi.login(email.trim(), password);
            if (data?.accessToken) {
                handleSuccess(data);
            } else {
                setError('Đăng nhập thất bại');
            }
        } catch (err) {
            setError(getApiErrorMessage(err, 'Email hoặc mật khẩu không đúng'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogleLogin = async () => {
        if (!sdkReady.google) return;
        setError('');
        try {
            const client = window.google.accounts.oauth2.initCodeClient({
                client_id: GOOGLE_CLIENT_ID,
                scope: 'openid email profile',
                ux_mode: 'popup',
                callback: async (response) => {
                    if (!response?.code) return;
                    try {
                        const data = await authApi.socialLogin('google', response.code, '', { tickerPdf: '' });
                        handleSuccess(data);
                    } catch (err) {
                        setError(getApiErrorMessage(err, 'Đăng nhập Google thất bại'));
                    }
                },
            });
            client.requestCode();
        } catch {
            setError('Không khởi tạo được Google Login');
        }
    };

    const handleFacebookLogin = async () => {
        if (!sdkReady.facebook || !window.FB) return;
        setError('');
        window.FB.login(
            (response) => {
                if (response.authResponse?.accessToken) {
                    const fbAccessToken = response.authResponse.accessToken;
                    authApi
                        .socialLoginRaw({
                            provider: 'facebook',
                            accessToken: fbAccessToken,
                            referralCode: '',
                            property: { tickerPdf: '' },
                        })
                        .then(handleSuccess)
                        .catch((err) => setError(getApiErrorMessage(err, 'Đăng nhập Facebook thất bại')));
                }
            },
            { scope: 'public_profile,email' },
        );
    };

    const handleAppleLogin = async () => {
        if (!sdkReady.apple || !window.AppleID) return;
        setError('');
        try {
            window.AppleID.auth.init({
                clientId: APPLE_CLIENT_ID,
                scope: 'name email',
                redirectURI: APPLE_REDIRECT_URI,
                usePopup: true,
            });
            const response = await window.AppleID.auth.signIn();
            const code = response?.authorization?.code;
            if (!code) return;
            const userName = response?.user?.name;
            const name = userName ? [userName.firstName, userName.lastName].filter(Boolean).join(' ') : '';
            const data = await authApi.socialLoginRaw({
                provider: 'apple',
                client: APPLE_CLIENT || undefined,
                code,
                name: name || undefined,
                referralCode: '',
                property: { tickerPdf: '' },
            });
            handleSuccess(data);
        } catch (err) {
            if (err?.error !== 'popup_closed_by_user') {
                setError(getApiErrorMessage(err, 'Đăng nhập Apple thất bại'));
            }
        }
    };

    const handleSocialLogin = (provider) => {
        if (provider === 'google') return handleGoogleLogin();
        if (provider === 'facebook') return handleFacebookLogin();
        if (provider === 'apple') return handleAppleLogin();
    };

    return (
        <div className="fixed inset-0 z-50 flex flex-col overflow-y-auto" style={{ background: '#f8f8f7' }}>
            <div className="flex-1 flex flex-col items-center justify-center px-4 py-10">
                <SimplizeLogo className="h-[35px] w-auto mb-[32px]" />
                
                <div className="w-full max-w-[404px] bg-white rounded-[16px] p-[24px_32px] shadow-[0_0_20px_rgba(0,0,0,0.05)] border border-white">
                    {step === 'initial' ? (
                        <>
                            <div className="flex flex-col items-center mb-[32px]">
                                <h1 className="text-[28px] leading-[36px] font-bold text-[#111827] text-center mb-[8px]">
                                    Đăng nhập
                                </h1>
                                <p className="text-[16px] text-[#636369] text-center">
                                    Chào mừng bạn quay trở lại Nebula
                                </p>
                            </div>

                            <div className="space-y-[16px]">
                                {PROVIDERS.map((provider) => (
                                    <button
                                        key={provider.id}
                                        type="button"
                                        onClick={() => handleSocialLogin(provider.id)}
                                        className="w-full flex items-center h-[48px] px-[16px] rounded-[12px] border-[0.5px] border-[#ececed] bg-white text-[16px] font-medium text-[#111827] shadow-[0_0_20px_rgba(0,0,0,0.05)] hover:opacity-80 active:opacity-70 transition-opacity cursor-pointer"
                                    >
                                        <span className="w-[20px] flex justify-center shrink-0">{provider.icon}</span>
                                        <span className="flex-1 text-center pr-[20px]">Tiếp tục với {provider.label}</span>
                                    </button>
                                ))}
                            </div>

                            <div className="flex items-center my-[16px]">
                                <div className="flex-1 h-px bg-[#ececed]" />
                                <span className="px-[16px] text-[13px] text-[#636369]">hoặc</span>
                                <div className="flex-1 h-px bg-[#ececed]" />
                            </div>

                            <form onSubmit={handleEmailContinue} className="space-y-[16px]">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Nhập địa chỉ email của bạn..."
                                    className="w-full h-[48px] px-[16px] rounded-[12px] border border-[#ececed] bg-white text-[16px] text-[#111827] focus:outline-none focus:border-[#999] placeholder:text-[#a0a0a5] transition-colors"
                                    required
                                />
                                <button
                                    type="submit"
                                    className="w-full h-[48px] bg-[#111827] text-white rounded-[12px] text-[16px] font-medium hover:bg-opacity-90 active:scale-[0.98] transition-all cursor-pointer"
                                >
                                    Tiếp tục
                                </button>
                            </form>
                        </>
                    ) : (
                        <>
                            <div className="flex flex-col items-center mb-[32px]">
                                <h1 className="text-[28px] leading-[36px] font-bold text-[#111827] text-center mb-[8px]">
                                    Đăng nhập
                                </h1>
                                <p className="text-[16px] text-[#636369] text-center">
                                    Chào mừng bạn quay trở lại Nebula
                                </p>
                            </div>

                            <form onSubmit={handleLogin} className="space-y-[16px]">
                                <div 
                                    className="w-full h-[48px] px-[16px] flex items-center rounded-[12px] border border-[#ececed] bg-[#f5f5f5] text-[16px] text-[#111827] cursor-pointer hover:bg-[#eee] transition-colors"
                                    onClick={() => setStep('initial')}
                                >
                                    <span className="flex-1 truncate">{email}</span>
                                    <svg className="w-4 h-4 text-[#a0a0a5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                </div>

                                <div className="relative">
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Nhập mật khẩu..."
                                        className="w-full h-[48px] px-[16px] rounded-[12px] border border-[#ececed] bg-white text-[16px] text-[#111827] focus:outline-none focus:border-[#999] placeholder:text-[#a0a0a5] transition-colors"
                                        required
                                        autoFocus
                                    />
                                    <div className="mt-[12px] flex justify-end">
                                        <Link to="/forgot-password" size="14" className="text-[14px] font-medium text-[#636369] hover:text-[#111827] transition-colors">
                                            Quên mật khẩu?
                                        </Link>
                                    </div>
                                </div>

                                {error && <p className="text-[13px] text-red-500 mt-2">{error}</p>}

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full h-[48px] mt-[8px] rounded-[12px] text-white text-[16px] font-medium transition-all cursor-pointer ${isSubmitting || !password ? 'bg-[#999] opacity-50' : 'bg-[#111827] hover:bg-opacity-90 active:scale-[0.98]'}`}
                                >
                                    {isSubmitting ? 'Đang đăng nhập...' : 'Đăng nhập'}
                                </button>
                            </form>
                        </>
                    )}

                    <div className="mt-[18px] text-center">
                        <span className="text-[14px] text-[#636369]">
                            Bạn chưa có tài khoản?{' '}
                            <Link to="/register" className="text-[#111827] font-[500] hover:underline">
                                Đăng ký miễn phí
                            </Link>
                        </span>
                    </div>
                </div>
            </div>

            <footer className="pb-6 px-6 flex flex-col items-center gap-4">
                <div className="flex flex-col items-center gap-1">
                    <span className="text-[13px] text-[#a0a0a5]">from</span>
                    <SimplizeOriginalLogo className="h-[18px] w-auto text-[#111827]" />
                </div>
                <div className="flex items-center gap-6 text-[13px] text-[#a0a0a5] text-center justify-center">
                    <a href="/terms" className="underline hover:text-[#111827] transition-colors">
                        Điều khoản dịch vụ
                    </a>
                    <a href="/privacy" className="underline hover:text-[#111827] transition-colors">
                        Chính sách quyền riêng tư
                    </a>
                    <span>&copy;{new Date().getFullYear()} Simplize</span>
                </div>
            </footer>
        </div>
    );
}
